import React, { useCallback, useEffect, useRef, useState } from 'react';
import cc from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useHistory } from 'react-router-dom';
import { FORM_ERROR } from 'final-form';
import { AxiosStatus } from 'api/axiosConfig';
import ErrorPage from 'ui/ErrorPage/ErrorPage';
import LoaderPage from 'ui/LoaderPage/LoaderPage';
import NoData from 'ui/structure/NoData/NoData';
import LoaderForTable from 'ui/LoaderForTable/LoaderForTable';
import TablePagination from 'ui/structure/TablePagination/TablePagination';
import DateEvent from 'ui/TableInputs/DateEvent/DateEvent';
import ApproveSection from 'ui/TableButtons/Approve_RejectButton/ApproveReject';
import { StatusModal, OTPVerificationModal } from 'components/modals';
import LinkToDetail from 'ui/TableInputs/LinkToDetail/LinkToDetail';
import api from 'api/api';
import { FormType as FormTypeOTP } from 'components/modals/OTPVerificationModal/OTPVerificationModal';
import SourceAccountClick from 'ui/TableInputs/SourceAccountEvent/SourceAccountEvent';
import BeneficiaryClick from 'ui/TableInputs/BeneficiaryEvent/BeneficiaryEvent';
import TransactionDetails from 'components/modals/TransactionDetails/TransactionDetails';
import TransactionsRejectionModal from 'components/modals/TransactionRejectionModal/TransactionRejectionModal';
import { ITransactionField } from 'ui/structure/TransactionCard';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import routePaths from 'routes/routePaths';
import { NETWORK_REQUIRED } from 'utils/constants';
import { IApiResponse } from 'utils/models';
import { formattedDate, amountFormatter } from 'utils/formatters';
import { getTransactionsArray } from '../../TransferMapper';
import {
  IPendingTransfer,
  IApiPendingTransfer,
  IPendingRejection,
  IRejectTransferBody,
  ISubmitTransferBody,
} from '../../Transfers.model';
import noDataImg from 'assets/img/no_data_pending.png';

const confirmationValues = [
  'Transfer ID:',
  'Source Account:',
  'Schedule ID:',
  'Destination Account:',
  'Beneficiary Name:',
  'Narration:',
  'Amount:',
];

const PendingToFABTransfer = () => {
  // Loading states
  const [loadingStatus, setLoadingStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const pendingLoading = loadingStatus === AxiosStatus.PENDING;
  const successLoaded = loadingStatus === AxiosStatus.SUCCESS;
  const failureLoaded = loadingStatus === AxiosStatus.FAILURE;
  const [loading, setLoading] = useState(false);
  const [showNoDataWindow, setShowNoDataWindow] = useState(false);
  const [isOpenTxnModal, setOpenTxnModal] = useState(false);
  const [transactionModal, setTransactionModal] = useState<IPendingTransfer>();
  const [confirmationData, setConfirmationData] = useState<ITransactionField[]>([]);
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const [OTPModalError, setOTPModalError] = useState<IOTPError | null>(null);
  const [isOTPModalOpen, setOTPModalOpen] = useState(false);
  const [isRejectionModalOpen, SetRejectionModalOpen] = useState(false);
  const gridRef = useRef<AgGridReact<IPendingTransfer>>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [rowData, setRowData] = useState<IPendingTransfer[]>([]);

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.pendingRequests);
  };

  // Requesting Data for Table
  const requestDataForTable = () => {
    gridRef.current?.api?.showLoadingOverlay();
    const requestBody = {
      transType: 'LOCAL-TRANSFER',
      pageNumber,
      pageSize,
    };
    api
      .post('transfer/pendingTransactions', requestBody)
      .then((data: IApiPendingTransfer) => {
        setRowData(data.transactions);
        setTotalRows(data.size);
        if (data.size === 0) {
          setShowNoDataWindow(true);
        } else {
          setShowNoDataWindow(false);
        }
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setLoadingStatus(AxiosStatus.FAILURE);
        gridRef.current?.api.hideOverlay();
      });
  };
  useEffect(requestDataForTable, [pageNumber, pageSize]);

  // TODO fix double request for first render to display NoData component
  useEffect(() => {
    setLoadingStatus(AxiosStatus.PENDING);
    const requestBody = {
      transType: 'LOCAL-TRANSFER',
      pageNumber,
      pageSize,
    };
    api
      .post('transfer/pendingTransactions', requestBody)
      .then((data: IApiPendingTransfer) => {
        setLoadingStatus(AxiosStatus.SUCCESS);
        if (data.size === 0) {
          setShowNoDataWindow(true);
        } else {
          setShowNoDataWindow(false);
        }
      })
      .catch((error: IApiResponse) => {
        setLoadingStatus(AxiosStatus.FAILURE);
        console.error(error);
      });
  }, [pageNumber, pageSize]);

  // State for ApproveModal
  const handleClickNameLink = (data: IPendingTransfer) => () => {
    setTransactionModal(data);
    setOpenTxnModal(true);
    const cardValues = {
      ...data,
    };
    setConfirmationData(getTransactionsArray(cardValues, undefined, undefined, confirmationValues));
  };
  const handleCloseTxnModal = () => {
    setTransactionModal(undefined);
    setOpenTxnModal(false);
  };

  const closeOTPModal = () => {
    setOTPModalOpen(false);
    setSubmitStatus(AxiosStatus.PENDING);
  };
  const closeRejectionModal = () => {
    SetRejectionModalOpen(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    setOTPModalOpen(false);
  }, []);

  //  Handle Submit approval
  const handleSubmitApprove = (data: IPendingTransfer, otp: string) => {
    const body: ISubmitTransferBody = {
      transactionId: data.transactionId,
      password: otp,
      passwordType: 'OTP',
      transType: 'LOCAL-TRANSFER',
      requestType: 'SINGLE',
      approvalStatus: 'APPROVE',
    };
    setLoading(true);
    return api.post('transfer/approval', body);
  };

  // Handle Rejection with Reasons
  const handleSubmitReject = (data: IPendingTransfer, reason: IPendingRejection) => {
    setSubmitStatus(AxiosStatus.PENDING);
    const body: IRejectTransferBody = {
      transactionId: data.transactionId,
      reason: reason.rejectionReason,
      transType: 'LOCAL-TRANSFER',
      approvalStatus: 'REJECT',
      requestType: 'SINGLE',
    };
    setLoading(true);
    api
      .post('transfer/rejection', body)
      .then((response: IApiPendingTransfer) => {
        setLoading(false);
        closeRejectionModal();
        setSubmitStatus(AxiosStatus.SUCCESS);
        setSuccessMsg(response.description);
        requestDataForTable();
      })
      .catch((error: IApiResponse) => {
        setErrorMsg(error.description || NETWORK_REQUIRED);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        gridRef.current?.api.hideOverlay();
      });
  };

  // Handle Approval with OTP SUBMIT
  const onOtpSubmit = useCallback(
    (value: IPendingTransfer) => (otp: string | undefined, form: FormTypeOTP | undefined) => {
      setSubmitStatus(AxiosStatus.PENDING);
      api
        .post(`customer/otp/verify/${otp || ''}/false/${String(sessionStorage.getItem('userName'))}`)
        .then(() => {
          handleSubmitApprove(value, String(otp))
            .then((response: IApiResponse) => {
              setLoading(false);
              closeOTPModal();
              setSuccessMsg(response.description);
              setSubmitStatus(AxiosStatus.SUCCESS);
              requestDataForTable();
            })
            .catch((err: IApiResponse) => {
              onRejectedSubmit(err);
            });
        })
        .catch((error: IOTPError) => {
          setOTPModalError(error);
          if (error.failureCount === 0 && form) {
            form.initialize({ digit1: '', digit2: '', digit3: '', digit4: '', digit5: '' });
          }
          return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
        });
    },
    [],
  );
  const onResend = () => api.post('/customer/otp/generate');

  const handleApprove = (datas: IPendingTransfer) => () => {
    setOTPModalOpen(true);
    handleCloseTxnModal();
    setTransactionModal(datas);

    return api.post('/customer/otp/generate');
  };

  const handleReject = (datas: IPendingTransfer | undefined): void => {
    SetRejectionModalOpen(true);
    handleCloseTxnModal();
    setTransactionModal(datas);
  };

  // Columns & render
  const renderActions = (prop: ICellRendererParams<IPendingTransfer, number | string>) => {
    if (prop.data) {
      return <ApproveSection onClickApprove={handleApprove(prop.data)} onClickReject={() => handleReject(prop.data)} />;
    }
    return false;
  };

  const renderNameLink = (prop: ICellRendererParams<IPendingTransfer, number | string>) => {
    if (prop.data) {
      return <LinkToDetail content={String(prop.data?.transactionId)} onClick={handleClickNameLink(prop.data)} />;
    }
    return false;
  };

  const renderCreated = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data) {
      const formatDate = formattedDate(prop.data?.createDate);
      return <DateEvent name={prop.data?.genericTransType} date={formatDate} />;
    }
    return null;
  };

  const renderAccount = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data) {
      return <SourceAccountClick acctNumber={prop.data?.sourceAccount} acctType={prop.data?.SourceAccountType} />;
    }
    return null;
  };

  const renderAmount = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data) {
      return <div>{amountFormatter('ghs', false)(String(prop.data?.transactionAmount))}</div>;
    }
    return null;
  };

  const renderNarration = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data) {
      return <div>{prop.data?.transactionNarration}</div>;
    }
    return null;
  };

  const renderBeneficiary = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data) {
      const benName = prop.data?.destinationAccountName;
      if (!benName) return '';
      const nameParts = benName.split(' ');
      const firstAndMiddleNames = nameParts.slice(0, 2).join(' ');
      return (
        <BeneficiaryClick
          benNumber={prop.data.destinationAccountNo}
          benName={nameParts.length >= 2 ? firstAndMiddleNames : benName}
        />
      );
    }
    return null;
  };

  const columnDefs: ColDef[] = [
    { field: 'transferID', headerName: 'Transfer ID', flex: 1, minWidth: 195, cellRenderer: renderNameLink },
    { field: 'scheduleID', headerName: 'Schedule ID', flex: 1, minWidth: 190 },
    { field: 'sourceAccount', headerName: 'Source Account', flex: 1, minWidth: 130, cellRenderer: renderAccount },
    { field: 'beneficiary', headerName: 'Beneficiary', flex: 1, minWidth: 150, cellRenderer: renderBeneficiary },
    { field: 'totalAmount', headerName: 'Amount', flex: 1, minWidth: 130, cellRenderer: renderAmount },
    { field: 'narration', headerName: 'Narration', flex: 1, minWidth: 150, cellRenderer: renderNarration },
    { field: 'created', headerName: 'Created', flex: 1, minWidth: 200, cellRenderer: renderCreated },
    { field: 'action', headerName: '', width: 220, cellRenderer: renderActions },
  ];

  // Pagination & Filters handlers
  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  return (
    <>
      {pendingLoading && <LoaderPage />}
      {failureLoaded && <ErrorPage />}
      {successLoaded && (
        <>
          {/* <pre>{JSON.stringify(rowData, null, 2)}</pre> */}
          {showNoDataWindow ? (
            <NoData
              img={noDataImg}
              title="No pending requests yet"
              subtitle="Once you receive a request for approval, it appears here"
            />
          ) : (
            <>
              <div className={cc('tableWrapper', 'ag-theme-material')}>
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  loadingOverlayComponent={LoaderForTable}
                  columnDefs={columnDefs}
                  defaultColDef={{ resizable: true }}
                  suppressCellFocus
                  enableCellTextSelection
                  headerHeight={60}
                  rowHeight={70}
                  undoRedoCellEditing
                  suppressRowHoverHighlight
                  pagination
                  paginationPageSize={pageSize}
                  suppressPaginationPanel
                  suppressScrollOnNewData
                  domLayout="autoHeight"
                />
              </div>
              <TablePagination
                count={Math.ceil(totalRows / pageSize)}
                page={pageNumber}
                pageSize={pageSize}
                totalCount={totalRows}
                handlePageChange={handlePageChange}
                handlePageSizeChange={handlePageSizeChange}
              />
            </>
          )}
          {transactionModal && (
            <TransactionDetails
              isApproveReject
              open={isOpenTxnModal}
              onClickApprove={handleApprove(transactionModal)}
              onClickReject={() => handleReject(transactionModal)}
              onClose={handleCloseTxnModal}
              data={confirmationData}
            />
          )}
          {isOTPModalOpen && transactionModal && (
            <OTPVerificationModal
              open={isOTPModalOpen}
              onClose={closeOTPModal}
              onSubmit={onOtpSubmit(transactionModal)}
              onResend={onResend}
              OTPModalError={OTPModalError}
              loading={loading}
            />
          )}
          {isRejectionModalOpen && transactionModal && (
            <TransactionsRejectionModal
              open={isRejectionModalOpen}
              onClose={closeRejectionModal}
              onReject={(reason) => {
                handleSubmitReject(transactionModal, reason);
              }}
            />
          )}
          <StatusModal
            open={showStatusModal}
            status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
            onClose={onCloseStatusModal}
            onRetry={onCloseStatusModal}
            onRetryText="Close"
          >
            {submitStatus === AxiosStatus.SUCCESS ? successMsg : errorMsg}
          </StatusModal>
        </>
      )}
    </>
  );
};

export default PendingToFABTransfer;
